import type {
  SearchPayload,
  SearchResults,
  ProgressiveSearchPayload,
  AdminFilesResponse,
  FileContentResponse,
  UploadResponse,
  FetchUrlResponse,
  HealthCheckResponse,
  SearchOptionsResponse,
  DictionaryLookupResponse,
  DictionarySearchResponse,
  SpellcheckResponse,
  AutocompleteResponse,
  SuggestionsResponse,
  SynonymsResponse,
  RandomWordResponse,
  DictionaryStatsResponse
} from '@/types'

const API_BASE_URL = '/api'

class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.message || `Request failed with status ${response.status}`,
      response.status,
      errorData
    )
  }
  return response.json()
}

export const api = {
  /**
   * Perform a search on text content
   */
  async search(payload: SearchPayload): Promise<SearchResults> {
    const response = await fetch(`${API_BASE_URL}/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        searchString: payload.searchString,
        text: payload.text,
        fileId: payload.fileId,
        searchMode: payload.searchMode || 'literal',
        matchTypes: payload.matchTypes || ['exact'],
        // Number type for number search (digits, written, or both)
        ...(payload.numberType ? { numberType: payload.numberType } : {}),
        // Position options for position search mode
        ...(payload.positionOptions ? { positionOptions: payload.positionOptions } : {})
      })
    })

    return handleResponse<SearchResults>(response)
  },

  /**
   * Perform a progressive search (phase by phase)
   */
  async progressiveSearch(payload: ProgressiveSearchPayload): Promise<SearchResults> {
    const response = await fetch(`${API_BASE_URL}/search/progressive`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    return handleResponse<SearchResults>(response)
  },

  /**
   * Upload a text file
   */
  async uploadFile(file: File): Promise<UploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE_URL}/files/upload`, {
      method: 'POST',
      body: formData
    })

    return handleResponse<UploadResponse>(response)
  },

  /**
   * Fetch content from a URL
   */
  async fetchUrl(url: string): Promise<FetchUrlResponse> {
    const response = await fetch(`${API_BASE_URL}/files/fetch-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ url })
    })

    return handleResponse<FetchUrlResponse>(response)
  },

  /**
   * Get list of admin/sample files
   */
  async getAdminFiles(): Promise<AdminFilesResponse> {
    const response = await fetch(`${API_BASE_URL}/files/admin`)
    return handleResponse<AdminFilesResponse>(response)
  },

  /**
   * Get content of a specific admin file
   */
  async getFileContent(filename: string): Promise<FileContentResponse> {
    const response = await fetch(`${API_BASE_URL}/files/admin/${encodeURIComponent(filename)}`)
    return handleResponse<FileContentResponse>(response)
  },

  /**
   * Get available search options from the backend
   */
  async getSearchOptions(): Promise<SearchOptionsResponse> {
    const response = await fetch(`${API_BASE_URL}/search/options`)
    return handleResponse<SearchOptionsResponse>(response)
  },

  /**
   * Check API health status
   */
  async checkHealth(): Promise<HealthCheckResponse> {
    const response = await fetch(`${API_BASE_URL}/health`)
    return handleResponse<HealthCheckResponse>(response)
  },

  // ===== Dictionary API =====

  /**
   * Look up a word in the dictionary
   */
  async dictionaryLookup(word: string): Promise<DictionaryLookupResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/lookup/${encodeURIComponent(word)}`)
    return handleResponse<DictionaryLookupResponse>(response)
  },

  /**
   * Search for words matching a query (with optional search modes)
   */
  async dictionarySearch(
    query: string,
    limit = 20,
    options?: {
      searchMode?: 'literal' | 'position' | 'form'
      matchTypes?: Array<'exact' | 'prefix' | 'suffix' | 'contains'>
      positionOptions?: {
        skip: number[]
        repeat: number[]
        exact: number[]
        derivate: boolean
      }
      formPattern?: string
    }
  ): Promise<DictionarySearchResponse> {
    // Build query params
    const params = new URLSearchParams({
      q: query,
      limit: String(limit)
    })

    if (options?.searchMode) {
      params.append('searchMode', options.searchMode)
    }
    if (options?.matchTypes && options.matchTypes.length > 0) {
      params.append('matchTypes', JSON.stringify(options.matchTypes))
    }
    if (options?.positionOptions) {
      params.append('positionOptions', JSON.stringify(options.positionOptions))
    }
    if (options?.formPattern) {
      params.append('formPattern', options.formPattern)
    }

    const response = await fetch(`${API_BASE_URL}/dictionary/search?${params.toString()}`)
    return handleResponse<DictionarySearchResponse>(response)
  },

  /**
   * Check spelling of words
   */
  async spellcheck(words: string[]): Promise<SpellcheckResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/spellcheck`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ words })
    })
    return handleResponse<SpellcheckResponse>(response)
  },

  /**
   * Check spelling of text
   */
  async spellcheckText(text: string): Promise<SpellcheckResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/spellcheck`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ text })
    })
    return handleResponse<SpellcheckResponse>(response)
  },

  /**
   * Get autocomplete suggestions for a prefix
   */
  async autocomplete(prefix: string, limit = 10): Promise<AutocompleteResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/autocomplete/${encodeURIComponent(prefix)}?limit=${limit}`)
    return handleResponse<AutocompleteResponse>(response)
  },

  /**
   * Get spelling suggestions for a misspelled word
   */
  async getSuggestions(word: string, limit = 10): Promise<SuggestionsResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/suggestions/${encodeURIComponent(word)}?limit=${limit}`)
    return handleResponse<SuggestionsResponse>(response)
  },

  /**
   * Get synonyms and related words (thesaurus)
   */
  async getSynonyms(word: string): Promise<SynonymsResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/synonyms/${encodeURIComponent(word)}`)
    return handleResponse<SynonymsResponse>(response)
  },

  /**
   * Get a random word (word of the day)
   */
  async getRandomWord(): Promise<RandomWordResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/random`)
    return handleResponse<RandomWordResponse>(response)
  },

  /**
   * Get dictionary statistics
   */
  async getDictionaryStats(): Promise<DictionaryStatsResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary/stats`)
    return handleResponse<DictionaryStatsResponse>(response)
  }
}

export { ApiError }
export default api
